import { CommonModule } from '@angular/common';
import { Component, DestroyRef, OnInit, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  AnnouncementDto,
  AnnouncementType,
  CreateAnnouncementRequest,
  UpdateAnnouncementRequest,
} from '../../../core/models/announcement.models';
import { AnnouncementService } from '../../../core/services/announcement.service';
import {
  Bell,
  Bookmark,
  Edit,
  Gift,
  Info,
  LucideAngularModule,
  Megaphone,
  Newspaper,
  Pin,
  Plus,
  Trash2,
  TriangleAlert,
  X,
} from 'lucide-angular';
import { DatePickerModule } from 'primeng/datepicker';

const TYPE_OPTIONS: { label: string; value: AnnouncementType; icon: string }[] = [
  { label: "Ma'lumot", value: 'info', icon: '💡' },
  { label: 'Aksiya', value: 'promo', icon: '🎁' },
  { label: 'Ogohlantirish', value: 'warning', icon: '⚠️' },
  { label: 'Yangilik', value: 'news', icon: '📰' },
];

@Component({
  selector: 'app-admin-announcements',
  standalone: true,
  imports: [CommonModule, FormsModule, LucideAngularModule, DatePickerModule],
  templateUrl: './announcements.component.html',
})
export class AdminAnnouncementsComponent implements OnInit {
  private readonly svc = inject(AnnouncementService);
  private readonly destroyRef = inject(DestroyRef);

  items = signal<AnnouncementDto[]>([]);
  totalCount = signal(0);
  loading = signal(false);
  page = signal(1);
  readonly pageSize = 15;

  dialogVisible = false;
  editing: AnnouncementDto | null = null;
  saving = false;

  form: {
    title: string;
    body: string;
    type: AnnouncementType;
    isPinned: boolean;
    isActive: boolean;
    expiresAt: Date | null;
  } = this.emptyForm();

  readonly typeOptions = TYPE_OPTIONS;

  // icons
  Plus = Plus;
  Edit = Edit;
  Trash2 = Trash2;
  X = X;
  Megaphone = Megaphone;
  Bell = Bell;
  Info = Info;
  Gift = Gift;
  TriangleAlert = TriangleAlert;
  Newspaper = Newspaper;
  Pin = Pin;
  Bookmark = Bookmark;

  get totalPages() {
    return Math.max(1, Math.ceil(this.totalCount() / this.pageSize));
  }
  get isEdit() {
    return !!this.editing;
  }

  ngOnInit() {
    this.load();
  }

  load() {
    this.loading.set(true);
    this.svc
      .getAdminAnnouncements(this.page(), this.pageSize)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (res) => {
          this.items.set(res.data?.items ?? []);
          this.totalCount.set(res.data?.totalCount ?? 0);
          this.loading.set(false);
        },
        error: () => this.loading.set(false),
      });
  }

  openCreate() {
    this.editing = null;
    this.form = this.emptyForm();
    this.dialogVisible = true;
  }

  openEdit(ann: AnnouncementDto) {
    this.editing = ann;
    this.form = {
      title: ann.title,
      body: ann.body,
      type: ann.type,
      isPinned: ann.isPinned,
      isActive: ann.isActive,
      expiresAt: ann.expiresAt ? new Date(ann.expiresAt) : null,
    };
    this.dialogVisible = true;
  }

  closeDialog() {
    this.dialogVisible = false;
    this.editing = null;
  }

  save() {
    if (!this.form.title.trim() || !this.form.body.trim()) return;
    this.saving = true;

    const expiresAt = this.form.expiresAt ? this.form.expiresAt.toISOString() : null;

    if (this.isEdit) {
      const req: UpdateAnnouncementRequest = {
        title: this.form.title,
        body: this.form.body,
        type: this.form.type,
        isPinned: this.form.isPinned,
        isActive: this.form.isActive,
        expiresAt,
      };
      this.svc
        .update(this.editing!.id, req)
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe({
          next: () => {
            this.saving = false;
            this.closeDialog();
            this.load();
          },
          error: () => {
            this.saving = false;
          },
        });
    } else {
      const req: CreateAnnouncementRequest = {
        title: this.form.title,
        body: this.form.body,
        type: this.form.type,
        isPinned: this.form.isPinned,
        expiresAt,
      };
      this.svc
        .create(req)
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe({
          next: () => {
            this.saving = false;
            this.closeDialog();
            this.load();
          },
          error: () => {
            this.saving = false;
          },
        });
    }
  }

  toggle(ann: AnnouncementDto) {
    this.svc
      .toggle(ann.id, !ann.isActive)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({ next: () => this.load() });
  }

  delete(ann: AnnouncementDto) {
    if (!confirm(`"${ann.title}" xabarini o'chirasizmi?`)) return;
    this.svc
      .delete(ann.id)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({ next: () => this.load() });
  }

  goPage(p: number) {
    if (p < 1 || p > this.totalPages) return;
    this.page.set(p);
    this.load();
  }

  typeMeta(type: string) {
    return TYPE_OPTIONS.find((t) => t.value === type) ?? TYPE_OPTIONS[0];
  }

  typeColor(type: string): string {
    switch (type) {
      case 'promo':
        return 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300';
      case 'warning':
        return 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300';
      case 'news':
        return 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300';
      default:
        return 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300';
    }
  }

  formatDate(d: string | null): string {
    if (!d) return '—';
    return new Date(d).toLocaleDateString('uz-UZ', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    });
  }

  private emptyForm() {
    return {
      title: '',
      body: '',
      type: 'info' as AnnouncementType,
      isPinned: false,
      isActive: true,
      expiresAt: null,
    };
  }
}
